import React, { useEffect, useState } from "react";
import { Container, Row, Col, Form, Button } from "react-bootstrap";
import { useNavigate, useLocation } from "react-router-dom";
import ShipInfoListTable from "../components/ShipInfoListTable";
import { getErrorMessage, getPageDescription, getSuccessMessage, getUiInterface } from "../../../locales/translationUtils";
import NavBar, { NavBarItem } from "../../../components/NavBar";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import AlertOverlay from "../../../components/Alert";
import { useShipInfo } from "../../../apis/ShipInfo/ShipInfoProvider";
import Paginacao from "../../../types/paginacao";
import { ShipInfo } from "../../../types/shipinfo";
import { FaEdit, FaInfoCircle, FaBan } from "react-icons/fa";
import ShippingInfoModal, { ShippingData } from '../components/ShippingInfoModal';
import CancelConfirmationModal from "./CancelConfirmationModal";

const ShipInfoListView: React.FC = () => {
    const { shipInfos, apiResponse, fetchShipInfos, markShipInfoAsShipped, cancelShipping } = useShipInfo();
    const navigate = useNavigate();
    const [alert, setAlert] = useState<{ message: string; variant: "success" | "danger" | "warning" | "info" } | null>(null);
    const [showModal, setShowModal] = useState(false);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [cancelId, setCancelId] = useState<number | null>(null);
    const [selectedData, setSelectedData] = useState<ShippingData | null>(null);
    const [selectedId, setSelectedId] = useState<number | null>(null);
    
    const handleCloseModal = () => setShowModal(false);

    const handleInfoClick = (id: string) => {
        const selected = (shipInfos ?? []).find(info => info.id === id);

        if (selected) {
            setSelectedId(Number(id));
            setSelectedData({
                mawb: selected.master || "",
                hawb: selected.house || "",
                shipDate: selected.shipDate?.split("T")[0] || "",
                arrivalDate: selected.arrival?.split("T")[0] || "",
            });
            setShowModal(true);
        }
    };

    const handleCancelClick = (id: string) => {
        setCancelId(Number(id));
        setShowCancelModal(true);
    };

    const handleConfirmCancel = async () => {
        if (cancelId !== null) {
            try {
                await cancelShipping(cancelId);
                setAlert({ message: getSuccessMessage("cancelShipping", { id: cancelId }), variant: "success" });
                fetchShipInfos(paginacao, searchParams);
            } catch (error) {
                setAlert({ message: getErrorMessage("cancelShippingError"), variant: "danger" });
            } finally {
                setShowCancelModal(false);
                setCancelId(null);
            }
        }
    };

    const handleSubmit = async (data: ShippingData) => {
        if (selectedId !== null) {
            try {
                await markShipInfoAsShipped(selectedId, data);
                setAlert({ message: getSuccessMessage("editShipInfo", { id: selectedId }), variant: "success" });
                fetchShipInfos(paginacao, searchParams);
            } catch (error) {
                setAlert({ message: getErrorMessage("editShipInfoError"), variant: "danger" });
            }
        } else {
            setAlert({ message: getErrorMessage("editNoId"), variant: "warning" });
        }

        setShowModal(false); 
    };

    const formatDate = (date: Date) => {
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    };

    const [searchParams, setSearchParams] = useState({
        shipped: true,
        campo: "dawb",
        valor: "",
        shippedBy: "",
        destination: "",
        freightType: "",
        releasedBy: "",
        consignee: "",
        to: "",
        releaseDate: "",
        master: "",
        house: "",
        shipDate: "",
        arrival: "",
    });

    const [paginationModel, setPaginationModel] = React.useState({
        pageSize: 20,
        page: 0,
    });

    const [paginacao, setPaginacao] = React.useState({
        pageNum: 0,
        quantityPerPage: 20
    } as Paginacao);
    
   const filterOptions = [
        { value: "dawb", label: "DAWB" },
        { value: "description", label: "Description" },
        { value: "ponumber", label: "PO Number" },
        { value: "wrnumber", label: "WR Number" },
        { value: "supplier", label: "Supplier" },
        { value: "invoicenumber", label: "Invoice Number" }
    ];

   useEffect(() => {
        const newPaginacao = {
            ...paginacao, 
            pageNum: paginationModel.page,
            quantityPerPage: paginationModel.pageSize
        };
        setPaginacao(newPaginacao);
        fetchShipInfos(newPaginacao, searchParams);
    }, [paginationModel]);

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();

        const valor = (e.currentTarget as any)[1].value;
        const updatedSearchParams = { ...searchParams, valor };

        const filters = Object.fromEntries(
            Object.entries(updatedSearchParams)
            .filter(([_, value]) => value !== "")
        );

        fetchShipInfos(paginacao, filters);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setSearchParams(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const navItems: NavBarItem[] = [
        { label: "Home", route: "/", iconName: "house-door" },
        { label: "Release List", route: "/Shipinfo", iconName: "list" }
    ];

    const safeValueGetter = (field: keyof ShipInfo) => (params: GridRenderCellParams<ShipInfo>) => {
        const value = params?.row?.[field];
        return value ? String(value) : "-";
    };

    const columns: GridColDef<ShipInfo>[] = [
    {
        field: "actions",
        headerName: "",
        width: 110,
        sortable: false,
        filterable: false,
        renderCell: (params: GridRenderCellParams<ShipInfo>) => (
        <div className="d-flex justify-content-center mt-1">
            <Button
            variant="link"
            size="lg"
            onClick={() => handleCancelClick(params.row.id)}
            className="p-1"
            >
            <FaBan className="text-danger" />
            </Button>
            <Button
            variant="link"
            size="lg"
            onClick={() => handleInfoClick(params.row.id)}
            className="p-1"
            >
            <FaEdit className="text-info" />
            </Button>
            <Button
            variant="link"
            size="lg"
            onClick={() => handleNavigation(`/shipinfo/${params.row.id}`)}
            className="p-1"
            >
            <FaInfoCircle className="text-primary" />
            </Button>
        </div>
        ),
    },
    {
        field: "shippedBy",
        headerName: "Shipped By",
        width: 180,
        valueGetter: (params: GridRenderCellParams<ShipInfo>) =>
          params?.row?.shippedBy || "-",
        renderCell: (params: GridRenderCellParams<ShipInfo>) => (
          <span style={{ color: "black" }}>{params?.row?.shippedBy || "-"}</span>
        ),
      },
      {
        field: "destination",
        headerName: "Destination",
        width: 180,
        valueGetter: (params: GridRenderCellParams<ShipInfo>) =>
          params?.row?.destination?.description || "-",
        renderCell: (params: GridRenderCellParams<ShipInfo>) => (
          <span style={{ color: "black" }}>{params?.row?.destination?.description || "-"}</span>
        ),
      },
      {
        field: "freightType",
        headerName: "Freight Type",
        width: 180,
        valueGetter: (params: GridRenderCellParams<ShipInfo>) =>
          params?.row?.freightType?.description || "-",
        renderCell: (params: GridRenderCellParams<ShipInfo>) => (
          <span style={{ color: "black" }}>{params?.row?.freightType?.description || "-"}</span>
        ),
      },
      {
        field: "releasedBy",
        headerName: "Released by",
        width: 180,
        valueGetter: (params: GridRenderCellParams<ShipInfo>) =>
          params?.row?.releasedBy?.fullName || "-",
        renderCell: (params: GridRenderCellParams<ShipInfo>) => (
          <span style={{ color: "black" }}>{params?.row?.releasedBy?.fullName || "-"}</span>
        ),
      },
      {
        field: "consignee",
        headerName: "Consignee",
        width: 300,
        valueGetter: (params: GridRenderCellParams<ShipInfo>) =>
          params?.row?.consignee?.name || "-",
        renderCell: (params: GridRenderCellParams<ShipInfo>) => (
          <span style={{ color: "black" }}>{params?.row?.consignee?.name || "-"}</span>
        ),
      },
      {
        field: "to",
        headerName: "To",
        width: 180,
        valueGetter: (params: GridRenderCellParams<ShipInfo>) =>
          params?.row?.to?.name || "-",
        renderCell: (params) => {
          if (!params?.row?.to?.name) return <span>-</span>;
          return <span style={{ color: "black" }}>{params.row.to.name}</span>;
        },
      },
      {
        field: "released",
        headerName: "Release date",
        width: 180,
        valueGetter: (params: GridRenderCellParams<ShipInfo>) =>
          params?.row?.released ?? "-",
        valueFormatter: (params: any) => {
          const dateStr = params?.value;
          if (!dateStr) return "-";
          try {
            const dateValue = new Date(dateStr);
            return dateValue.toLocaleDateString();
          } catch {
            return "-";
          }
        },
        renderCell: (params: GridRenderCellParams<ShipInfo>) => {
          if (!params?.row?.released) return <span>-</span>;
          try {
            const dateValue = new Date(params.row.released);
            return <span style={{ color: "black" }}>{dateValue.toLocaleDateString()}</span>;
          } catch {
            return <span>-</span>;
          }
        },
      },
        {
            field: "master",
            headerName: "Master",
            width: 160,
            valueGetter: (params: GridRenderCellParams<ShipInfo>) =>
                params?.row?.master || "-",
            renderCell: (params) => {
                if (!params?.row?.master) return <span>-</span>;
                return <span style={{ color: "black" }}>{params.row.master}</span>;
            },
        },
        {
            field: "house",
            headerName: "House",
            width: 160,
            valueGetter: (params: GridRenderCellParams<ShipInfo>) =>
                params?.row?.house || "-",
            renderCell: (params) => {
                if (!params?.row?.house) return <span>-</span>;
                return <span style={{ color: "black" }}>{params.row.house}</span>;
            },
        },
        {
            field: "shipDate",
            headerName: "Ship Date",
            width: 160,
            valueGetter: (params: GridRenderCellParams<ShipInfo>) =>
                params?.row?.shipDate || "-",
            renderCell: (params) => {
                if (!params?.row?.shipDate) return <span>-</span>;
                return <span style={{ color: "black" }}>{params.row.shipDate}</span>;
            },
        },
        {
            field: "arrival",
            headerName: "Arrival",
            width: 160,
            valueGetter: (params: GridRenderCellParams<ShipInfo>) =>
                params?.row?.arrival || "-",
            renderCell: (params) => {
                if (!params?.row?.arrival) return <span>-</span>;
                return <span style={{ color: "black" }}>{params.row.arrival}</span>;
            },
        }
    ];

    const handleNavigation = (route: string) => {
        navigate(route);
    };

    return (
         <Container fluid style={{ height: 'calc(100vh - 200px)', overflow: 'auto' }}>
            <ShippingInfoModal
                show={showModal}
                onClose={handleCloseModal}
                onSubmit={handleSubmit}
                initialData={selectedData ?? undefined}
            />

            <CancelConfirmationModal
                show={showCancelModal}
                onClose={() => setShowCancelModal(false)}
                onConfirm={handleConfirmCancel}
                shipId={cancelId}
            />
            
            <NavBar items={navItems} />
            <div className="mb-3"></div>
            <h2>{getUiInterface("search")}</h2>
            <div className="mb-3"></div>
            
            {alert && (
                <AlertOverlay
                    message={alert.message}
                    variant={alert.variant}
                    onClose={() => setAlert(null)}
                />
            )}

            <div className="mb-4 p-3 bg-light rounded">
                <h5 className="mb-3">{getUiInterface("searchFor")}</h5>
                
                <Form onSubmit={handleSearch}>
                <Row className="mb-3">
                    <Col md={3}>
                        <Form.Group controlId="campo">
                            <Form.Select
                                name="campo"
                                value={searchParams.campo}
                                onChange={handleInputChange}
                            >
                                {filterOptions.map(option => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    </Col>
                    <Col md={3}>
                        <Form.Group controlId="valor">
                            <Form.Control
                                type="text"
                                name="valor"
                            />
                        </Form.Group>
                    </Col>
                    <Col md={2} className="d-flex align-items-end">
                        <Button variant="primary" type="submit">
                            Search
                        </Button>
                    </Col>
                </Row>
            </Form>
            </div>

            <h3 className="mb-3">{getPageDescription("releaseList")}</h3>
            <ShipInfoListTable
                rows={Array.isArray(shipInfos) ? shipInfos : []}
                columns={columns}
                getRowClassName={(params) => (params.indexRelativeToCurrentPage % 2 === 0 ? "even-row" : "odd-row")}
                apiResponse={apiResponse} 
                paginationModel={paginationModel}
                setPaginationModel={setPaginationModel}
            />
        </Container>
    );
};

export default ShipInfoListView;